import { useEffect } from "react";

const TRANSLATION_GUARD_ATTR = "data-translation-guard";
const STRUCTURAL_NODE_ATTR = "data-structural-node";
const STRUCTURAL_ORDER_ATTR = "data-structural-order";
const DEFAULT_LABEL_ATTR = "data-default-label";
const LABEL_TEXT_ATTR = "data-label-text";

let removeChildPatchRefs = 0;
let originalNodeRemoveChild = null;

const installRemoveChildPatch = () => {
  if (typeof Node === "undefined" || removeChildPatchRefs < 0) {
    return () => {};
  }

  removeChildPatchRefs += 1;

  if (removeChildPatchRefs > 1) {
    return () => {
      removeChildPatchRefs -= 1;
    };
  }

  const prototype = Node.prototype;
  const nativeRemoveChild = prototype.removeChild;

  if (typeof nativeRemoveChild !== "function") {
    return () => {
      removeChildPatchRefs -= 1;
    };
  }

  originalNodeRemoveChild = nativeRemoveChild;

  // Google Translate occasionally re-parents nodes which causes React to call
  // removeChild on an element that no longer contains the node. The patched
  // version quietly ignores those stale removals to keep React from crashing.
  prototype.removeChild = function patchedRemoveChild(child) {
    if (!child || !(child instanceof Node)) {
      return nativeRemoveChild.call(this, child);
    }

    if (child.parentNode !== this) {
      return child;
    }

    try {
      return nativeRemoveChild.call(this, child);
    } catch (error) {
      if (error && error.name === "NotFoundError") {
        return child;
      }
      throw error;
    }
  };

  return () => {
    removeChildPatchRefs -= 1;

    if (removeChildPatchRefs === 0 && originalNodeRemoveChild) {
      prototype.removeChild = originalNodeRemoveChild;
      originalNodeRemoveChild = null;
    }
  };
};

const sortByStructuralOrder = (a, b) => {
  const orderA = Number(a?.getAttribute?.(STRUCTURAL_ORDER_ATTR) || 0);
  const orderB = Number(b?.getAttribute?.(STRUCTURAL_ORDER_ATTR) || 0);
  return orderA - orderB;
};

const isElementNode = (node) => node?.nodeType === Node.ELEMENT_NODE;

const isGuardedElement = (node) =>
  node instanceof HTMLElement && node.getAttribute(TRANSLATION_GUARD_ATTR) === "true";

const cleanupUnexpectedChildren = (element) => {
  const nodesToRemove = [];
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      nodesToRemove.push(node);
      return;
    }
    if (isElementNode(node) && node instanceof HTMLElement) {
      if (!node.hasAttribute(STRUCTURAL_NODE_ATTR)) {
        nodesToRemove.push(node);
      }
    }
  });
  nodesToRemove.forEach((node) => {
    if (node.parentNode === element) {
      element.removeChild(node);
    }
  });
};

const restoreRemovedNodes = (element, removedNodes, translatedLabel) => {
  const nodes = removedNodes.length > 0 ? removedNodes : [];

  nodes.sort(sortByStructuralOrder).forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    if (node.getAttribute(STRUCTURAL_NODE_ATTR) === "label") {
      if (translatedLabel) {
        node.textContent = translatedLabel;
      } else {
        const fallback = node.getAttribute(DEFAULT_LABEL_ATTR);
        if (fallback) {
          node.textContent = fallback;
        }
      }
    }

    if (node.parentNode !== element) {
      element.appendChild(node);
    }
  });

  if (!translatedLabel) {
    const currentLabel = element.getAttribute(LABEL_TEXT_ATTR);
    if (currentLabel) {
      const labelNode = element.querySelector('[data-structural-node="label"]');
      if (labelNode) {
        labelNode.textContent = currentLabel;
      }
    }
  }
};

const ensureGuardMetadata = (element) => {
  if (!isGuardedElement(element)) {
    return;
  }

  element.querySelectorAll('[data-structural-node="label"]').forEach((node) => {
    if (!node.hasAttribute(DEFAULT_LABEL_ATTR)) {
      node.setAttribute(DEFAULT_LABEL_ATTR, node.textContent || "");
    }
  });
};

export default function useTranslationStructureGuard(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const restoreRemoveChild = installRemoveChildPatch();

    const initializeGuardMetadata = () => {
      root.querySelectorAll(`[${TRANSLATION_GUARD_ATTR}="true"]`).forEach((element) => {
        ensureGuardMetadata(element);
      });
    };

    initializeGuardMetadata();

    const observer = new MutationObserver((mutations) => {
      let metadataDirty = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (isElementNode(node) && node instanceof HTMLElement) {
            if (isGuardedElement(node)) {
              ensureGuardMetadata(node);
            }
            node
              .querySelectorAll?.(`[${TRANSLATION_GUARD_ATTR}="true"]`)
              .forEach((child) => ensureGuardMetadata(child));
          }
        });

        const target = mutation.target;
        if (!isGuardedElement(target)) {
          return;
        }

        const removedStructuralNodes = Array.from(mutation.removedNodes).filter(
          (node) =>
            isElementNode(node) &&
            node instanceof HTMLElement &&
            node.hasAttribute(STRUCTURAL_NODE_ATTR),
        );

        const hasIcon = target.querySelector('[data-structural-node="icon"]');
        const hasLabel = target.querySelector('[data-structural-node="label"]');

        if (removedStructuralNodes.length === 0 && hasIcon && hasLabel) {
          return;
        }

        const translatedLabel = (target.textContent || "").trim();

        cleanupUnexpectedChildren(target);

        restoreRemovedNodes(target, removedStructuralNodes, translatedLabel);

        metadataDirty = true;
      });

      if (metadataDirty) {
        initializeGuardMetadata();
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      restoreRemoveChild();
    };
  }, [rootRef]);
}
